/**
 * iManage's landing and About page, on the shared AppLandingPage.
 *
 * Until the person connects, the area shows the landing in place of the page
 * asked for (IManageArea), with the way in that fits their state: connect,
 * finish a sign-in, ask an admin to set iManage up, or retry an unreachable
 * service. Once connected, `/$org/apps/imanage/about` is the same page with
 * the connection — status, Reconnect, Disconnect — in place of the call to
 * connect.
 *
 * iManage's status names no account, so the connection reads as "your own
 * iManage account" rather than a user name it does not have.
 *
 * @module components/apps/imanage/IManageLanding
 */
import type { IManageStatus } from "@amoreg/contracts/imanage";
import { Link } from "@tanstack/react-router";
import { Loader2Icon, RotateCwIcon, SettingsIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { AppLandingPage } from "../AppLandingPage";
import { AppIcon, useAppEntries } from "../AppParts";
import { appDefinition, appLanding } from "../appRegistry";
import { IntegrationIcon } from "../integrations/IntegrationIcon";
import { Button } from "../../ui/button";
import { useIManageArea, useIManageConnect } from "./imanageAreaContext";
import { IManagePageHeader } from "./IManageLayout";
import { allowedOperationsPhrase, imanageLandingState } from "./IManageLanding.logic";

const APP_ID = "imanage";

function IManageTile({ organizationId }: { readonly organizationId: string }) {
  const { entries } = useAppEntries(organizationId);
  const entry = entries.find((candidate) => candidate.id === APP_ID);
  const className = "size-14 rounded-2xl";
  return entry ? (
    <AppIcon entry={entry} className={className} />
  ) : (
    <IntegrationIcon
      integration={{ id: APP_ID, name: "iManage", iconUrl: null, toolkitSlug: null }}
      className={className}
    />
  );
}

/** What Amoreg may do with the connection, from the organization's own scope. */
function AccessNote({ status }: { readonly status: IManageStatus | null }) {
  const allowed = status ? allowedOperationsPhrase(status.operations) : null;
  return (
    <>
      <p>
        You sign in to iManage with your own account. Amoreg sees only what your iManage permissions
        allow, and other members connect their own accounts.
      </p>
      {status?.configured ? (
        <p>
          {allowed
            ? `Your organization lets Amoreg ${allowed} in iManage.`
            : "Your organization has not allowed any iManage operations yet."}
          {status.canUpload ? " Nothing is saved to iManage until you confirm the exact file." : ""}
        </p>
      ) : null}
      <p>You can disconnect at any time.</p>
    </>
  );
}

/** The landing, shown in place of any iManage page until the person connects. */
export function IManageLanding({
  organizationId,
  orgSegment,
  status,
  statusError,
  canManage,
  onRetry,
}: {
  readonly organizationId: string;
  readonly orgSegment: string;
  /** Null when the status could not be read. */
  readonly status: IManageStatus | null;
  readonly statusError: string | null;
  readonly canManage: boolean;
  readonly onRetry: () => void;
}) {
  const state = imanageLandingState(status);
  const { connect, busy, error } = useIManageConnect(organizationId, onRetry);
  const settingsLink = canManage ? (
    <Button
      variant="ghost-muted"
      size="sm"
      render={<Link to="/$org/apps/imanage/settings" params={{ org: orgSegment }} />}
      data-app-landing-settings=""
    >
      <SettingsIcon aria-hidden />
      Settings
    </Button>
  ) : null;

  let actions: ReactNode;
  switch (state) {
    case "unavailable":
      actions = (
        <ActionRow note={statusError ?? "The iManage service could not be reached."} tone="error">
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RotateCwIcon aria-hidden />
            Try again
          </Button>
        </ActionRow>
      );
      break;
    case "not-configured":
      actions = canManage ? (
        <ActionRow note="Register Amoreg with your firm's iManage Cloud first. Then each member connects their own account.">
          <Button
            size="sm"
            render={<Link to="/$org/apps/imanage/settings" params={{ org: orgSegment }} />}
            data-imanage-setup=""
          >
            Set up iManage
          </Button>
        </ActionRow>
      ) : (
        <ActionRow note="An organization admin needs to set up iManage before you can connect.">
          <Button size="sm" disabled>
            Connect iManage
          </Button>
        </ActionRow>
      );
      break;
    case "pending":
      actions = (
        <ActionRow
          note={
            <span className="inline-flex items-center gap-1.5" role="status">
              <Loader2Icon aria-hidden className="size-3.5 animate-spin" />
              Finish signing in in the iManage tab. This page opens iManage when you are done.
            </span>
          }
        >
          <Button size="sm" variant="outline" onClick={connect} disabled={busy} data-imanage-connect="">
            {busy ? <Loader2Icon aria-hidden className="animate-spin" /> : null}
            Sign in again
          </Button>
          {settingsLink}
        </ActionRow>
      );
      break;
    case "ready":
    case "connected":
      actions = (
        <ActionRow note="Opens iManage's sign-in in a new tab.">
          <Button size="sm" onClick={connect} disabled={busy} data-imanage-connect="">
            {busy ? <Loader2Icon aria-hidden className="animate-spin" /> : null}
            Connect iManage
          </Button>
          {settingsLink}
        </ActionRow>
      );
      break;
  }

  return (
    <AppLandingPage
      appId={APP_ID}
      name={appDefinition(APP_ID)?.name ?? "iManage Cloud"}
      landing={appLanding(APP_ID)!}
      mark={<IManageTile organizationId={organizationId} />}
      orgSegment={orgSegment}
      standalone
      actions={
        <>
          {actions}
          {error ? (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {error}
            </p>
          ) : null}
        </>
      }
      access={<AccessNote status={status} />}
    />
  );
}

function ActionRow({
  note,
  tone = "muted",
  children,
}: {
  readonly note: ReactNode;
  readonly tone?: "muted" | "error";
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <p className={cn("text-xs", tone === "error" ? "text-destructive" : "text-muted-foreground")}>
        {note}
      </p>
    </div>
  );
}

/**
 * The person's connection, with Reconnect and Disconnect: on the About page
 * and in Settings, so the two never disagree.
 */
export function IManageConnectionCard({
  organizationId,
  status,
  onChanged,
}: {
  readonly organizationId: string;
  readonly status: IManageStatus;
  readonly onChanged: () => void;
}) {
  const connection = useIManageConnect(organizationId, onChanged);
  const state = imanageLandingState(status);
  return (
    <div className="flex flex-col gap-2" data-imanage-connection={state}>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border/70 px-4 py-3">
        <span
          aria-hidden
          className={cn(
            "size-2 shrink-0 rounded-full",
            state === "connected" ? "bg-success" : "bg-muted-foreground/40",
          )}
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[13px] font-medium text-foreground">
            {state === "connected"
              ? "Connected"
              : state === "pending"
                ? "Signing in"
                : state === "not-configured"
                  ? "Not set up"
                  : "Not connected"}
          </span>
          <span className="text-xs text-muted-foreground">
            {state === "connected"
              ? "With your own iManage account and permissions."
              : state === "pending"
                ? "Finish signing in in the iManage tab."
                : state === "not-configured"
                  ? "Available once an admin sets up the iManage application."
                  : "Connect your own iManage account to use it here."}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            variant={state === "connected" ? "outline" : "default"}
            disabled={connection.busy || !status.configured}
            onClick={connection.connect}
            data-imanage-reconnect=""
          >
            {connection.busy ? <Loader2Icon aria-hidden className="animate-spin" /> : null}
            {state === "connected" ? "Reconnect" : "Connect iManage"}
          </Button>
          {state === "connected" || state === "pending" ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={connection.busy}
              onClick={() => void connection.disconnect()}
              data-imanage-disconnect=""
            >
              Disconnect
            </Button>
          ) : null}
        </span>
      </div>
      {connection.error ? (
        <p role="alert" className="text-xs text-destructive">
          {connection.error}
        </p>
      ) : null}
    </div>
  );
}

/** `/$org/apps/imanage/about`: the landing, with the connection in place of the call to connect. */
export function IManageAboutPage() {
  const { organizationId, orgSegment, status, refreshStatus } = useIManageArea();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <IManagePageHeader title={<span className="text-[15px] font-semibold">About</span>} />
      <AppLandingPage
        appId={APP_ID}
        name={appDefinition(APP_ID)?.name ?? "iManage Cloud"}
        landing={appLanding(APP_ID)!}
        mark={<IManageTile organizationId={organizationId} />}
        orgSegment={orgSegment}
        standalone={false}
        actions={
          <IManageConnectionCard
            organizationId={organizationId}
            status={status}
            onChanged={refreshStatus}
          />
        }
        access={<AccessNote status={status} />}
      />
    </div>
  );
}
